import { adt, gpAdt } from '@/util/useData'
import { getServfee } from '@/apis/shopcart'
import { AdtCfgList, getShopLogistics } from '@/apis/setup'
import { isD2C, isJA, isKO, getWarehouseCode, isMultiStore } from '@/store'
import { t } from '@/plugins/i18n'
import { COUNTRY } from '@/global'

const adtMapP = (warehouseCode?:string) =>
  adt(warehouseCode).then((res) => {
    return (res.productAdditionList ?? []).reduce((map: any, v: any) => {
      map[v.additionConfigId] = v
      return map
    }, {})
  })

interface ADT {
  additionConfigId: number;
  ingredientsSku?: string;
}
interface Sku {
  buyQuantity: number;
  orderQuantity?: number;
  totalPrice: number;
  cartAdditionList?: ADT[];
  combinationProductAdditionList?: ADT[];
}

const rule: any = {
  '-1': () => 0,
  1: (item: Sku, adt: any) => {
    let amount = 0
    // 抽检的情况下需要根据 商品数量x抽检比例线上取整 再乘以费用
    if (adt.spotCheckFlag === 1 && adt.spotCheckRatio) {
      const num = Math.ceil(item.buyQuantity * adt.spotCheckRatio)
      // 如果不在范围内就取定价cost
      const cost = adt.spotCheckConfigDTOList?.find((i:any) => i.startQuantity <= num && num < i.endQuantity)?.cost || adt.cost || 0
      amount = cost * num
    } else {
      amount = (adt.cost || 0) * item.buyQuantity
    }
    return amount
  },
  3: (item: Sku, adt: any) => (adt.cost / 100) * item.totalPrice,
  5: (item: Sku, adt: any) => adt.cost,
  10: (item: Sku, adt: any) => adt.cost * (item.buyQuantity ? 1 : 0)
}

async function calc (item: Sku, MapP: Promise<any>) {
  const map = await MapP
  return (item.cartAdditionList ?? []).reduce((acc, v) => {
    const cfg = { ...map[v.additionConfigId], ...v }
    return acc + rule[cfg.calculateConfigId ?? '-1'](item, cfg)
  }, 0)
}

// 计算附加项费
export const calcAdt = (item: Sku, countryCode?:string): Promise<number> => {
  const warehouseCode = getWarehouseCode(countryCode === COUNTRY.KR)
  return calc(item, adtMapP(warehouseCode))
}

// 计算组合商品附加项费
export const calcGpAdt = (item: Sku, source?:number, countryCode?:string): Promise<number> => {
  const warehouseCode = getWarehouseCode(countryCode === COUNTRY.KR)
  const gp = gpAdt(source, warehouseCode).then((res) => {
    return (res.productAdditionList ?? []).reduce((map: any, v: any) => {
      map[v.additionConfigId] = v
      return map
    }, {})
  })
  const t = {
    ...item,
    buyQuantity: item.buyQuantity || 0,
    cartAdditionList: item.combinationProductAdditionList
  }
  return calc(t, gp)
}

// 计算手续费
export const calcSer = (item: Sku, channel: number): Promise<number> => {
  const mapP = getServfee(channel).then((res) => {
    // 这里套用模板数据结构
    item.cartAdditionList = res.data
    return (res.data ?? []).reduce((map: any, v: any) => {
      map[v.additionConfigId] = v
      return map
    }, {})
  })
  return calc(item, mapP)
}

// 商品加购前查询对应类目附加项 isMultiGoods 是否韩国商品
export const setAdt = (() => {
  // let req: any = null
  return async (
    cateId: string,
    listName = 'cartAdditionList',
    flagName = 'noAdditionalFlag',
    combinationProductAdt = [],
    noAdditionalFlag = undefined,
    isMultiGoods?:boolean
  ): Promise<any> => {
    let data: any = []
    if (noAdditionalFlag === 1) {
      return {
        [flagName]: 1,
        [listName]: []
      }
    } else if (combinationProductAdt.length) {
      return {
        [flagName]: 0,
        [listName]: combinationProductAdt
      }
    }
    // if (!req) {
    //   req = AdtCfgList({
    //     warehouseCode: getWarehouseCode(isMultiGoods)
    //   }).then((res) => {
    //     req = null
    //     return res
    //   })
    // }
    await AdtCfgList({
      warehouseCode: getWarehouseCode(isMultiGoods)
    }).then((res: any) => {
      if (res.success) {
        res.data.forEach((v: any) => {
          if (v.additionScene === 0 && !data.length) {
            data = v.noAdditionalFlag || v.additionItemList
          } else if (v.additionScene === 1 && v.categoryAdditionList) {
            if (!v.categoryAdditionList) return
            v.categoryAdditionList.forEach((v2: any) => {
              if (v2.categoryStaus && v2.categoryCateId === cateId) {
                data = v.noAdditionalFlag || v.additionItemList
              }
            })
          }
        })
      }
    })
    return {
      [flagName]: data === 1 ? 1 : 0,
      [listName]: data === 1 ? [] : data
    }
  }
})()

// 加购前获取默认发货附加项
export const getDeli = (warehouseCode:string): any =>
  getShopLogistics().then(async (res) => {
    console.log(res)
    if (res.success) {
      const adtRes = await adt(warehouseCode).then((data) => {
        return data.deliveryAdditionList.map((v: any) => {
          const t = { ...v }
          return t
        })
      })
      const data = res.data.additionItemList ?? []
      const arr = adtRes.filter((v: any) => data.find((v2: any) => v2.additionCode === v.additionCode)).map((i:any) => {
        return {
          ...i,
          // 如果匹配辅料需要反显
          ingredientsSku: data.find((v2: any) => v2.additionCode === i.additionCode)?.ingredientsSku ?? ''
        }
      })
      const dft = adtRes.filter((v: any) => v.additionCode === (isD2C() ? 42 : 23))
      return arr?.length ? arr : dft
    }
  })

export const getAdt = async (list: any, type: any, warehouseCode?:string): Promise<any> => {
  list = list ?? []
  const adtRes = await adt(warehouseCode).then((data) => {
    return data[type ? 'deliveryAdditionList' : 'productAdditionList'].map((v: any) => {
      const f = list.find((v2: any) => v2.additionId === v.additionId)
      if (f) {
        return { ...f, ...v }
      } else {
        return false
      }
    })
  })
  const arr = adtRes.filter((v: any) => v)
  return arr
}

// 整理附加项名称展示 展示抽检
export const montageAddition = (additionList:any) => {
  return additionList?.map((v:any) => {
    const washText = []
    if (v.additionCode === 1) {
      if ((v.washedlabelAdditionId && v.washedlabelAdditionId !== '-') || (v.washedlabelName && v.washedlabelName !== '-')) {
        washText.push(v.washedlabelName)
      }
      if (v.washedlabelSpecification && v.washedlabelSpecification !== '-') {
        washText.push(v.washedlabelSpecification)
      }
    }
    return v.additionNameJp + (v.additionExtraName !== '-' && v.additionExtraName ? `(${v.additionExtraName})` : '') + (v.spotCheckFlag === 1 && v.spotCheckRatio ? `(${t('抽检')}：${Math.round(v.spotCheckRatio * 100)}%)` : '') + (washText.length > 0 ? `(${washText.join('/')})` : '')
  })?.join(',') || ''
}
interface ADITION_TYPE {
  additionCode: number;
  washedlabelSpecification?: string;
  additionId: number
  confirmWashedlabelSpecification?: number | null
}
// 判断是否包含水洗标附加项但未填写sku 购物车用
export const isConfirmWashAdditionCustomerSku = (additionList:ADITION_TYPE[] | '-') => {
  if (!isJA()) return false
  if (!additionList || additionList === '-') return false
  const adt = additionList?.find((v) => v.additionCode === 1)
  if (adt && adt.confirmWashedlabelSpecification !== 1) {
    return true
  }
  return false
}

// 判断是否包含水洗标附加项但未填写sku 编辑组合商品
export const isNoWashAdditionCustomerSku = (additionList:ADITION_TYPE[] | '-') => {
  if (!isJA()) return false
  if (!additionList || additionList === '-') return false
  const adt = additionList?.find((v) => v.additionCode === 1)
  if (adt && (!adt.washedlabelSpecification || adt.washedlabelSpecification === '-')) {
    return true
  }
  return false
}
